// apps/web/src/pages/RmsManualPage.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { apiPost, apiGet } from "../utils/apiClient";
import { useUserContext } from "../contexts/UserContext";
import dayjs from "dayjs";
import { isFreePlanUser, isLightPlanUser} from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";

type DoneMonthInfo = {
    firstDownloadedAt?: { _seconds: number };
};

type ManualStatus =
    | null
    | {
        status: string; // "running" | "success" | "error" など
        yearMonth?: string;
        doneMonths?: Record<string, DoneMonthInfo>;
        errorMessage?: string;
    };

/* ===============================
   フリープラン ダイアログ
================================ */
const FreePlanUpgradeDialog = ({
    open,
    onClose,
    isSuperAdmin,
}: {
    open: boolean;
    onClose: () => void;
    isSuperAdmin: boolean;
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
            <div className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className="bg-[#4a72c4] px-7 pb-5 pt-6">
                    <div className="mb-2 text-[11px] font-medium tracking-[0.08em] text-[#cfdcf5]">
                        スタンダードプラン以上の機能
                    </div>
                    <div className="text-[17px] font-semibold leading-[1.5] text-white">
                        過去月データ取得はスタンダードプラン以上で
                        <br />
                        ご利用いただけます
                    </div>
                </div>

                <div className="px-7 py-6">
                    <div className="mb-[18px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                過去データを遡って取得
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                今期の全データをまとめて確認できます
                            </div>
                        </div>

                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                前期・今期の利益比較
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                楽天スーパーSALEなど前年同期と比較可能
                            </div>
                        </div>
                    </div>

                    <p className="mb-5 text-[13px] leading-[1.7] text-[#555]">
                        「去年の楽天スーパーSALEより今年は儲かったのか？」過去月のデータを取得して、
                        <strong className="font-semibold text-[#333]">
                            前期・今期の利益比較
                        </strong>
                        をまるごと把握できます。
                    </p>

                    <div className="flex gap-[10px] border-t border-[#eee] pt-4">
                        <button
                            className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
                            onClick={() => openUpgradeForm("過去データ取得")}
                        >
                            プランをアップグレードする →
                        </button>

                        <button
                            className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
                            onClick={onClose}
                        >
                            閉じる
                        </button>
                    </div>

                    {isSuperAdmin && (
                        <div className="mt-3 text-[11px] text-slate-500">
                            ※ superAdmin は例外的に実行可能ですが、通常ユーザー向け表示としては制限対象です。
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function RmsManualPage() {
    const user = useUserContext();

    const isRestrictedPlan = useMemo(() => isFreePlanUser(user) || isLightPlanUser(user), [user]);
    const isSuperAdmin = Boolean((user as any)?.superAdmin);

    // フリープランとライトプランは不可 / superAdmin は例外で可
    const canUseDataPeriodFetch = !isRestrictedPlan || isSuperAdmin;

    const denyMessage = "【スタンダードプラン以上でご利用いただけます。】";

    // 一般お問い合わせフォーム（Sidebar と同じ）
    const CONTACT_FORM_URL =
        "https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header";

    const [showContactGuide, setShowContactGuide] = useState(false);

    const [syncRunning, setSyncRunning] = useState(false);
    const syncRunningRef = useRef(false);

    const [yearMonth, setYearMonth] = useState("");
    const [manualStatus, setManualStatus] = useState<ManualStatus>(null);
    const [showFreePlanDialog, setShowFreePlanDialog] = useState(false);

    const fetchStatus = useCallback(async () => {
        if (!user?.tenantId || !user?.companyId || !user?.shopUrl) return;

        const qs = new URLSearchParams({
            tenantId: user.tenantId,
            accountId: user.companyId,
            shopId: user.shopUrl,
        }).toString();

        const res = await apiGet(`/api/rms/manual/status?${qs}`);
        if (!res.ok) {
            setManualStatus(null);
            return;
        }

        const json = (await res.json()) as any;
        setManualStatus((json ?? null) as ManualStatus);
    }, [user?.tenantId, user?.companyId, user?.shopUrl]);

    useEffect(() => {
        fetchStatus().catch(console.error);
    }, [fetchStatus]);

    useEffect(() => {
        if (manualStatus?.status !== "running") return;

        const t = setInterval(() => {
            fetchStatus().catch(console.error);
        }, 3000);

        return () => clearInterval(t);
    }, [manualStatus?.status, fetchStatus]);

    useEffect(() => {
        if (isRestrictedPlan) {
            setShowFreePlanDialog(true);
        }
    }, [isRestrictedPlan, isSuperAdmin]);

    const selectableMonths = useMemo(() => {
        const list: string[] = [];
        const base = dayjs().startOf("month").subtract(1, "month");
        for (let i = 0; i < 24; i++) {
            list.push(base.subtract(i, "month").format("YYYY-MM"));
        }
        return list;
    }, []);

    const doneMonths = manualStatus?.doneMonths ?? {};
    const isFirstDownload = yearMonth && !doneMonths[yearMonth];

    const formatTs = (ts?: { _seconds: number }) => {
        if (!ts?._seconds) return "";
        return new Date(ts._seconds * 1000).toLocaleString("ja-JP");
    };

    // 【仕様変更 2026-07-25 小野】課金(¥5,000)は自動でなく手動案内のため、
    // ここからの API 直接実行はやめ、お問い合わせフォームへの誘導に変更
    function runManual() {
        if (!canUseDataPeriodFetch) {
            alert(denyMessage);
            return;
        }

        if (!yearMonth) {
            alert("取得する月を選択してください");
            return;
        }

        setShowContactGuide(true);
    }

    async function runManualSync() {
        if (syncRunningRef.current) return;

        if (!canUseDataPeriodFetch) {
            alert(denyMessage);
            return;
        }

        syncRunningRef.current = true;
        setSyncRunning(true);

        try {
            if (!yearMonth) {
                alert("取得する月を選択してください");
                return;
            }

            const message =
                `【テスト用：同期取得】\n` +
                `${yearMonth} を同期で取得します。\n` +
                `処理が長い場合、画面が待ち状態になります。\n実行しますか？`;

            if (!window.confirm(message)) return;

            await apiPost("/api/rms/manual/sync", { yearMonth });

            await fetchStatus();
        } finally {
            syncRunningRef.current = false;
            setSyncRunning(false);
        }
    }

    const anyRunning = syncRunning;
    const shouldBlockUi = isRestrictedPlan && !isSuperAdmin;

    return (
        <div>
            <FreePlanUpgradeDialog
                open={showFreePlanDialog}
                onClose={() => setShowFreePlanDialog(false)}
                isSuperAdmin={isSuperAdmin}
            />

            {/* 過去月データ取得のお問い合わせ誘導 */}
            {showContactGuide && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
                    <div className="w-full max-w-[440px] rounded-xl bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                        <div className="mb-3 text-[16px] font-semibold text-[#333]">
                            過去月データ取得のお申し込み
                        </div>

                        <p className="mb-3 text-sm leading-[1.8] text-[#555]">
                            <strong className="font-semibold text-[#333]">{yearMonth}</strong>{" "}
                            のデータ取得をご希望の場合は、1度 ec vision へお問い合わせください。
                            <br />
                            担当者が確認のうえ、取得手続きをご案内いたします。
                        </p>

                        <p className="mb-5 text-xs leading-[1.7] text-gray-500">
                            ※ 月次データの<strong>初回取得時のみ ¥5,000</strong> の料金が発生します。
                            <br />
                            ※ 取得済み月の再取得では追加料金はかかりません。
                        </p>

                        <div className="flex gap-[10px] border-t border-[#eee] pt-4">
                            <button
                                className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
                                onClick={() =>
                                    window.open(CONTACT_FORM_URL, "_blank", "noopener,noreferrer")
                                }
                            >
                                お問い合わせフォームを開く →
                            </button>

                            <button
                                className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
                                onClick={() => setShowContactGuide(false)}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={shouldBlockUi ? "pointer-events-none select-none opacity-50" : ""}>
                <div className="w-full max-w-md">
                    {manualStatus?.status === "running" && (
                        <div className="p-3 mb-4 bg-yellow-100 text-yellow-800 rounded">
                            {manualStatus.yearMonth} のデータを取得中です…
                        </div>
                    )}
                    {manualStatus?.status === "success" && (
                        <div className="p-3 mb-4 bg-green-100 text-green-800 rounded">
                            データ取得が完了しました！
                        </div>
                    )}
                    {manualStatus?.status === "error" && (
                        <div className="p-3 mb-4 bg-red-100 text-red-800 rounded">
                            エラー：{manualStatus.errorMessage}
                        </div>
                    )}

                    <div className="border rounded-lg p-4 bg-white shadow-sm space-y-4">
                        <div>
                            <label className="block mb-1">取得する月</label>
                            <select
                                value={yearMonth}
                                onChange={(e) => setYearMonth(e.target.value)}
                                className="border p-2 rounded w-full"
                                disabled={anyRunning}
                            >
                                <option value="">選択してください</option>
                                {selectableMonths.map((ym) => {
                                    const info = doneMonths[ym];
                                    return (
                                        <option key={ym} value={ym}>
                                            {ym}
                                            {info
                                                ? `（取得済み：${formatTs(info.firstDownloadedAt)}）`
                                                : "（初回取得：¥5,000）"}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <p className="text-sm text-gray-500">
                            ※ 月次データの<strong>初回取得時のみ ¥5,000</strong>の料金が発生します。<br />
                            ※ 取得済み月の再実行では追加料金はかかりません。
                        </p>

                        <button
                            onClick={runManual}
                            className={`px-4 py-2 rounded w-full text-white
                                ${anyRunning ? "opacity-60 cursor-not-allowed" : ""}
                                ${isFirstDownload ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
                                `}
                            disabled={anyRunning}
                        >
                            データ取得を申し込む
                        </button>

                        {user.superAdmin && (
                            <button
                                onClick={runManualSync}
                                className={`px-4 py-2 rounded w-full text-white
                                    ${anyRunning ? "opacity-60 cursor-not-allowed" : ""}
                                    bg-gray-700 hover:bg-gray-800
                                `}
                                disabled={anyRunning}
                                title="テスト用：同期実行（処理が長いと待ち状態になります）"
                            >
                                {syncRunning ? "同期実行中…" : "同期で取得（テスト用）"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}